import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface ShareLink {
  key: string;
  label: string;
  url: string;
}

interface EventShareLinksProps {
  eventId: string;
}

// Share links for an event across every surface it is reachable from
// (SweatSocial website, SweatSocial app, and the studio's whitelabel app when
// it has one). Shown after event creation and persistently in edit mode.
const EventShareLinks: React.FC<EventShareLinksProps> = ({ eventId }) => {
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getEventShareLinks(eventId)
      .then((res: any) => {
        if (!cancelled) setLinks(res.links || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load share links');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading share links…</Typography>
      </Box>
    );
  }

  if (error) {
    return <Typography variant="body2" color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle2">Share Links</Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        {links.map((link) => (
          <Box
            key={link.key}
            display="flex"
            alignItems="center"
            gap={1}
            p={1}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Box flex={1} minWidth={0}>
              <Typography variant="body2" fontWeight={600}>{link.label}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {link.url}
              </Typography>
            </Box>
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={() => copyToClipboard(link.url)}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open">
              <IconButton size="small" component="a" href={link.url} target="_blank" rel="noopener noreferrer">
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default EventShareLinks;
